/**
 * Overview Section Header Component
 * Displays section title, match score, and action buttons (Save, Menu, Hide, Share)
 */

import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { BookmarkIcon, EyeSlashIcon, ArrowUpOnSquareIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkIconSolid } from '@heroicons/react/24/solid';
import { MatchDonutWithLoginPrompt } from '../../../../../components/MatchDonut';
import { Tooltip } from '../../../../../components/Tooltip';
import { EllipsesMenuDropdown } from './EllipsesMenuDropdown';
import { isDark } from '../utils/jobDetailUtils';
import type { DisplayJob } from '../../../../../types';
import type { ApplicationState } from '../../../../../redux/features/jobs/jobsSlice';
import type { MatchScoreData } from '../../../../../utils/data/calculate-job-match-score';

export interface OverviewSectionHeaderProps {
  displayJob: DisplayJob | null;
  isAuthenticated: boolean;
  isHydrated: boolean;
  matchScoreData: MatchScoreData | null;
  onLoginClick: () => void;
  isSaved: boolean;
  isApplied: boolean;
  isHidden: boolean;
  currentApplicationState: ApplicationState | null;
  ellipsisButtonState: {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
  } | null;
  isMenuOpen: boolean;
  menuRef: React.RefObject<HTMLDivElement>;
  effectiveBrandColour: string;
  useBrandColor: boolean;
  onSave: () => Promise<void>;
  onToggleHide: () => void;
  onShare: () => void;
  onSetApplicationState: (state: ApplicationState | null) => void;
  onToggleApplied: () => void;
  onMenuToggle: () => void;
}

export const OverviewSectionHeader: React.FC<OverviewSectionHeaderProps> = ({
  displayJob,
  isAuthenticated,
  isHydrated,
  matchScoreData,
  onLoginClick,
  isSaved,
  isApplied,
  isHidden,
  currentApplicationState,
  ellipsisButtonState,
  isMenuOpen,
  menuRef,
  effectiveBrandColour,
  useBrandColor,
  onSave,
  onToggleHide,
  onShare,
  onSetApplicationState,
  onToggleApplied,
  onMenuToggle,
}) => {
  const intl = useIntl();
  const matchScore = matchScoreData?.overall ?? null;

  const getSaveButtonStyle = () => {
    if (!isSaved) return {};
    return {
      backgroundColor: useBrandColor ? effectiveBrandColour : '#bbf7d0',
      borderColor: useBrandColor ? effectiveBrandColour : '#bbf7d0',
    };
  };

  const getSaveButtonHoverStyle = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (useBrandColor) {
      e.currentTarget.style.backgroundColor = effectiveBrandColour;
      e.currentTarget.style.borderColor = effectiveBrandColour;
      e.currentTarget.style.filter = 'brightness(0.92)';
    } else {
      e.currentTarget.style.backgroundColor = '#6ee7b7';
      e.currentTarget.style.borderColor = '#6ee7b7';
    }
  };

  const getSaveButtonOutStyle = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (useBrandColor) {
      e.currentTarget.style.backgroundColor = effectiveBrandColour;
      e.currentTarget.style.borderColor = effectiveBrandColour;
      e.currentTarget.style.filter = '';
    } else {
      e.currentTarget.style.backgroundColor = '#bbf7d0';
      e.currentTarget.style.borderColor = '#bbf7d0';
    }
  };

  return (
    <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
      <h2 id="overview-header" className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
        <FormattedMessage id="jobDetailPage.section.overview" defaultMessage="Overview" description="Tab label for job overview section showing key job information" />
      </h2>
      <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
        {/* Match Score Donut */}
        {displayJob && (isAuthenticated && isHydrated) && (
          <MatchDonutWithLoginPrompt
            score={matchScore}
            size={64}
            onLoginClick={onLoginClick}
            breakdown={matchScoreData ?? undefined}
          />
        )}

        {/* Save Button */}
        <button
          className={`border rounded-full w-11 h-11 flex items-center justify-center shadow transition-colors transition-transform duration-200 cursor-pointer ${
            isSaved ? '' : 'bg-white/90 dark:bg-gray-800/90 border-gray-200/60 dark:border-gray-700/60 hover:bg-gray-100/80 dark:hover:bg-gray-700/80 hover:scale-105 active:scale-95'
          }`}
          aria-label={
            isSaved
              ? intl.formatMessage({
                  id: 'hit.saved.detail',
                  defaultMessage: 'Saved',
                  description: 'Button label when job is saved on detail page'
                })
              : intl.formatMessage({
                  id: 'hit.save.detail',
                  defaultMessage: 'Save',
                  description: 'Button label to save a job on detail page'
                })
          }
          style={getSaveButtonStyle()}
          onMouseOver={isSaved ? getSaveButtonHoverStyle : undefined}
          onMouseOut={isSaved ? getSaveButtonOutStyle : undefined}
          onClick={onSave}
        >
          {isSaved ? (
            <BookmarkIconSolid className="h-5 w-5" style={{ color: useBrandColor ? (isDark(effectiveBrandColour) ? '#fff' : '#111827') : '#111827' }} />
          ) : (
            <BookmarkIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
          )}
        </button>

        {/* Ellipses Menu */}
        <div className="relative" ref={menuRef}>
          <Tooltip
            content={
              ellipsisButtonState
                ? ellipsisButtonState.label
                : intl.formatMessage({ id: 'hit.tooltip.moreOptions', defaultMessage: 'More options', description: 'Tooltip text for button that opens menu with additional job actions' })
            }
            position="top"
          >
            <button
              className={`border rounded-full w-11 h-11 flex items-center justify-center shadow transition-colors transition-transform duration-200 cursor-pointer ${
                ellipsisButtonState
                  ? ellipsisButtonState.color === 'purple'
                    ? 'bg-purple-50 dark:bg-purple-900/30 border-purple-200 dark:border-purple-700'
                    : ellipsisButtonState.color === 'green'
                    ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700'
                    : ellipsisButtonState.color === 'red'
                    ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700'
                    : ellipsisButtonState.color === 'emerald'
                    ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700'
                    : 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700'
                  : 'bg-white/90 dark:bg-gray-800/90 border-gray-200/60 dark:border-gray-700/60'
              } hover:bg-gray-100/80 dark:hover:bg-gray-700/80 hover:scale-105 active:scale-95`}
              aria-label={
                ellipsisButtonState
                  ? ellipsisButtonState.label
                  : intl.formatMessage({ id: 'hit.moreOptions', defaultMessage: 'More options', description: 'Button label for menu with additional job actions (save, hide, mark as applied, etc.)' })
              }
              onClick={onMenuToggle}
              type="button"
            >
              {ellipsisButtonState ? (() => {
                const IconComponent = ellipsisButtonState.icon;
                return (
                  <IconComponent
                    className={`h-5 w-5 ${
                      ellipsisButtonState.color === 'purple'
                        ? 'text-purple-700 dark:text-purple-300'
                        : ellipsisButtonState.color === 'green'
                        ? 'text-green-700 dark:text-green-300'
                        : ellipsisButtonState.color === 'red'
                        ? 'text-red-700 dark:text-red-300'
                        : ellipsisButtonState.color === 'emerald'
                        ? 'text-emerald-700 dark:text-emerald-300'
                        : 'text-blue-700 dark:text-blue-300'
                    }`}
                  />
                );
              })() : (
                <EllipsisVerticalIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              )}
            </button>
          </Tooltip>

          <EllipsesMenuDropdown
            isOpen={isMenuOpen}
            currentApplicationState={currentApplicationState}
            isApplied={isApplied}
            isHidden={isHidden}
            onSetApplicationState={onSetApplicationState}
            onToggleApplied={onToggleApplied}
            onToggleHide={onToggleHide}
            onClose={onMenuToggle}
          />
        </div>

        {/* Hide Button */}
        <button
          className={`border rounded-full w-11 h-11 flex items-center justify-center shadow transition-colors transition-transform duration-200 cursor-pointer ${
            isHidden ? '' : 'bg-white/90 dark:bg-gray-800/90 border-gray-200/60 dark:border-gray-700/60 hover:bg-gray-100/80 dark:hover:bg-gray-700/80 hover:scale-105 active:scale-95'
          }`}
          aria-label={
            isHidden
              ? intl.formatMessage({ id: 'hit.hidden', defaultMessage: 'Hidden', description: 'Button label when a job is currently hidden from search results' })
              : intl.formatMessage({ id: 'hit.hide.button', defaultMessage: 'Hide', description: 'Button label to hide a job' })
          }
          style={
            isHidden
              ? {
                  backgroundColor: '#9ca3af',
                  borderColor: '#9ca3af',
                }
              : {}
          }
          onClick={onToggleHide}
          type="button"
        >
          <EyeSlashIcon className={`h-5 w-5 ${isHidden ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'}`} />
        </button>

        {/* Share Button */}
        <button
          className="bg-white/90 dark:bg-gray-800/90 border border-gray-200/60 dark:border-gray-700/60 rounded-full w-11 h-11 flex items-center justify-center shadow hover:bg-gray-100/80 dark:hover:bg-gray-700/80 transition-colors transition-transform duration-200 hover:scale-105 active:scale-95 cursor-pointer"
          aria-label={intl.formatMessage({
            id: 'hit.share.detail',
            defaultMessage: 'Share',
            description: 'Button label to share a job on detail page'
          })}
          onClick={onShare}
        >
          <ArrowUpOnSquareIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
        </button>
      </div>
    </div>
  );
};
